import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/authService';

// Refresh token 60 seconds before expiry
const REFRESH_BUFFER_SECONDS = 60;
// Maximum retry attempts for network errors
const MAX_REFRESH_RETRIES = 3;
// Delay between retries (exponential backoff base)
const RETRY_BASE_DELAY_MS = 2000;

// Cross-tab refresh coordination. With refresh-token rotation enabled
// server-side, two tabs racing /refresh causes one to get a 401 and dump
// the user. The first tab to refresh broadcasts the new tokens; other tabs
// adopt them instead of issuing their own /refresh. localStorage is already
// shared across tabs, so the broadcast is just a signal that storage is fresh.
const AUTH_CHANNEL = 'webmail-auth-sync';
const authChannel: BroadcastChannel | null = (() => {
  try {
    return typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(AUTH_CHANNEL) : null;
  } catch {
    return null;
  }
})();
const broadcastTokensRefreshed = (): void => {
  try { authChannel?.postMessage({ type: 'tokens-refreshed', at: Date.now() }); } catch {}
};
const broadcastLogout = (): void => {
  try { authChannel?.postMessage({ type: 'logout', at: Date.now() }); } catch {}
};

export const useAuth = () => {
  const {
    token,
    refreshToken,
    isAuthenticated,
    isInitialized,
    setTokens,
    setInitialized,
    logout: storeLogout
  } = useAuthStore();

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshInProgressRef = useRef<Promise<void> | null>(null);
  const retryCountRef = useRef(0);

  // Perform token refresh with retry logic.
  // Returns:
  //   true  → refresh succeeded (or another tab already did it; we just
  //           adopted the fresh storage values).
  //   false → refresh token is genuinely invalid; caller should log out.
  // Throws nothing on transient infra errors — those become a `true` return
  // (we leave existing tokens in place and let the next attempt try again),
  // because forcing a logout on a 502/503 is the wrong UX.
  const performRefresh = useCallback(async (): Promise<boolean> => {
    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) {
      console.log('[Auth] No refresh token available');
      return false;
    }

    // Another tab might have just refreshed; if so, adopt and skip the call
    // entirely. This is the fix for the rotation race.
    const SHARED_FRESH_WINDOW_MS = 5000;
    if (Date.now() < stored.expiry - (REFRESH_BUFFER_SECONDS * 1000) + SHARED_FRESH_WINDOW_MS
        && stored.expiry - Date.now() > REFRESH_BUFFER_SECONDS * 1000) {
      // Token is comfortably fresh. Likely another tab refreshed for us.
      setTokens(stored.accessToken, stored.refreshToken);
      retryCountRef.current = 0;
      return true;
    }

    try {
      console.log('[Auth] Refreshing token...');
      const tokens = await authService.refreshToken(stored.refreshToken);
      setTokens(tokens.accessToken, tokens.refreshToken);
      broadcastTokensRefreshed();
      retryCountRef.current = 0;
      return true;
    } catch (error: any) {
      const msg: string = error?.message || '';
      // Terminal: server explicitly rejected the refresh token.
      if (msg.includes('Session expired')) {
        console.warn('[Auth] Refresh token rejected by server, logging out');
        return false;
      }
      // Transient: network outage or 5xx. Retry with backoff, and on final
      // failure leave tokens intact so the next user action / scheduled
      // attempt can recover.
      const isTransient = msg.startsWith('network:') || msg.startsWith('refresh transient:')
        || error.name === 'TypeError';
      if (isTransient && retryCountRef.current < MAX_REFRESH_RETRIES) {
        retryCountRef.current++;
        const delay = RETRY_BASE_DELAY_MS * Math.pow(2, retryCountRef.current - 1);
        console.log(`[Auth] Transient refresh error (${msg}), retry ${retryCountRef.current}/${MAX_REFRESH_RETRIES} in ${delay}ms`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return performRefresh();
      }

      console.warn('[Auth] Refresh failed transiently; keeping tokens for next attempt:', msg);
      // Treat unrecoverable transient as "still authenticated" to avoid the
      // forced-logout-on-blip footgun. The 401 handler in apiClient will
      // surface the real failure if the access token is actually rejected
      // on the next API call.
      return true;
    }
  }, [setTokens]);

  // Schedule automatic token refresh
  const scheduleTokenRefresh = useCallback(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) return;

    const now = Date.now();
    const expiresAt = stored.expiry;
    const refreshAt = expiresAt - (REFRESH_BUFFER_SECONDS * 1000);
    const delay = Math.max(refreshAt - now, 10000); // At least 10 seconds

    console.log(`[Auth] Scheduling token refresh in ${Math.round(delay / 1000)}s`);

    refreshTimerRef.current = setTimeout(async () => {
      const success = await performRefresh();
      if (success) {
        scheduleTokenRefresh(); // Schedule next refresh
      } else {
        // Session expired, will show login
        storeLogout();
        authService.clearTokens();
      }
    }, delay);
  }, [performRefresh, storeLogout]);

  // Check token on visibility change (tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible' && isAuthenticated) {
        console.log('[Auth] Tab became visible, checking token...');

        // Check if token is expired or about to expire
        if (authService.isTokenExpired(REFRESH_BUFFER_SECONDS)) {
          console.log('[Auth] Token expired while tab was hidden, refreshing...');
          const success = await performRefresh();
          if (success) {
            scheduleTokenRefresh();
          } else {
            storeLogout();
            authService.clearTokens();
          }
        } else {
          // Token is still valid, reschedule refresh (timer might have been throttled)
          scheduleTokenRefresh();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, performRefresh, scheduleTokenRefresh, storeLogout]);

  // Initialize authentication on mount
  useEffect(() => {
    const initAuth = async () => {
      if (isInitialized) return;

      console.log('[Auth] Initializing authentication...');
      try {
        const tokens = await authService.initializeAuth();

        if (tokens) {
          console.log('[Auth] Found valid tokens');
          setTokens(tokens.accessToken, tokens.refreshToken);
          scheduleTokenRefresh();
        } else {
          console.log('[Auth] No valid tokens found');
        }
      } catch (error) {
        console.error('[Auth] Initialization error:', error);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [isInitialized, setTokens, setInitialized, scheduleTokenRefresh]);

  // Login function
  const login = useCallback(async (username: string, password: string, otp?: string) => {
    const tokens = await authService.login({ username, password, otp });
    setTokens(tokens.accessToken, tokens.refreshToken);
    scheduleTokenRefresh();
    return tokens;
  }, [setTokens, scheduleTokenRefresh]);

  // Logout function — also tells other tabs to drop their session, so a user
  // hitting "Sign out" in one tab doesn't leave another tab still logged in.
  const logout = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    broadcastLogout();
    await authService.logout();
    storeLogout();
  }, [storeLogout]);

  // Listen for tokens-refreshed / logout broadcasts from other tabs and adopt
  // the change. Belt-and-suspenders: also listen to `storage` events for
  // browsers (or contexts) where BroadcastChannel isn't available.
  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      if (!ev?.data) return;
      if (ev.data.type === 'tokens-refreshed') {
        const stored = authService.getStoredTokens();
        if (stored?.accessToken && stored?.refreshToken) {
          setTokens(stored.accessToken, stored.refreshToken);
        }
      } else if (ev.data.type === 'logout') {
        storeLogout();
        authService.clearTokens();
      }
    };
    const onStorage = (ev: StorageEvent) => {
      if (ev.key && (ev.key.startsWith('webmail_'))) {
        const stored = authService.getStoredTokens();
        if (!stored) {
          storeLogout();
        } else {
          setTokens(stored.accessToken, stored.refreshToken);
        }
      }
    };
    authChannel?.addEventListener('message', onMessage);
    window.addEventListener('storage', onStorage);
    return () => {
      authChannel?.removeEventListener('message', onMessage);
      window.removeEventListener('storage', onStorage);
    };
  }, [setTokens, storeLogout]);

  // Manual token refresh (for retrying failed requests)
  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    const stored = authService.getStoredTokens();
    if (!stored?.refreshToken) return null;

    // Prevent concurrent refresh attempts
    if (refreshInProgressRef.current) {
      await refreshInProgressRef.current;
      return authService.getStoredTokens()?.accessToken || null;
    }

    refreshInProgressRef.current = (async () => {
      try {
        const tokens = await authService.refreshToken(stored.refreshToken);
        setTokens(tokens.accessToken, tokens.refreshToken);
        scheduleTokenRefresh();
      } catch (error) {
        console.error('[Auth] Manual refresh failed:', error);
        storeLogout();
        authService.clearTokens();
        throw error;
      } finally {
        refreshInProgressRef.current = null;
      }
    })();

    await refreshInProgressRef.current;
    return authService.getStoredTokens()?.accessToken || null;
  }, [setTokens, storeLogout, scheduleTokenRefresh]);

  // Redirect to admin portal login
  const redirectToLogin = useCallback(() => {
    authService.redirectToAdminLogin();
  }, []);

  return {
    token,
    refreshToken,
    isAuthenticated,
    isInitialized,
    login,
    logout,
    refreshAccessToken,
    redirectToLogin,
    adminPortalUrl: authService.getAdminPortalUrl()
  };
};
